import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface SkillStep {
  step: string;
  title: string;
  detail: string;
  icon: string;
}

interface ProfessionalSkillsProps {
  category: string;
  categoryIcon: string;
  skillName: string;
  difficulty: '入门' | '进阶' | '高级';
  steps: SkillStep[];
  keyTip: string;
  toolsUsed?: string[];
}

const DIFFICULTY_CONFIG = {
  '入门': { color: '#10b981', dots: 1 },
  '进阶': { color: '#f59e0b', dots: 2 },
  '高级': { color: '#ef4444', dots: 3 },
};

const DEFAULT_PROPS: ProfessionalSkillsProps = {
  category: '设计技能',
  categoryIcon: '🎨',
  skillName: 'Figma 组件化设计',
  difficulty: '进阶',
  steps: [
    { step: '01', title: '创建基础组件', detail: '使用 Auto Layout 构建可复用的原子组件', icon: '🧩' },
    { step: '02', title: '定义变体', detail: '通过 Variants 管理组件的不同状态和尺寸', icon: '🔀' },
    { step: '03', title: '构建组件库', detail: '将组件发布到团队共享库，统一设计语言', icon: '📚' },
    { step: '04', title: '实战应用', detail: '在实际项目中灵活调用组件，快速搭建页面', icon: '🚀' },
  ],
  keyTip: '组件命名要规范，建议使用 "类别/名称/状态" 格式',
  toolsUsed: ['Figma', 'FigJam', 'Tokens Studio'],
};

export const ProfessionalSkillsTemplate: React.FC<Partial<ProfessionalSkillsProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const diffConfig = DIFFICULTY_CONFIG[p.difficulty];

  // Animated scan line
  const scanLineY = interpolate(frame % 120, [0, 120], [-100, 2020]);

  // Header animation
  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 100 } });

  // Current active step (progressive highlight)
  const activeStepFloat = interpolate(frame, [80, 400], [0, p.steps.length - 0.01], { extrapolateRight: 'clamp' });
  const activeStep = Math.floor(activeStepFloat);

  // Tip animation
  const tipOpacity = interpolate(frame, [420, 450], [0, 1], { extrapolateRight: 'clamp' });
  const tipY = spring({ frame: frame - 420, fps, from: 30, to: 0, config: { damping: 20, stiffness: 100 } });

  return (
    <AbsoluteFill
      style={{
        background: '#0d1117',
        fontFamily: '-apple-system, "PingFang SC", "SF Mono", monospace',
        color: '#e6edf3',
        overflow: 'hidden',
      }}
    >
      {/* Scan line effect */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: scanLineY,
          height: 2,
          background: 'linear-gradient(90deg, transparent, rgba(56, 189, 248, 0.4), transparent)',
          filter: 'blur(1px)',
        }}
      />

      {/* Grid background */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(56, 189, 248, 0.06) 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }}
      />

      {/* Top accent line */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: 'linear-gradient(90deg, #38bdf8, #818cf8, #c084fc)',
        }}
      />

      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 60,
          left: 60,
          right: 60,
          opacity: headerProgress,
          transform: `translateY(${(1 - headerProgress) * -30}px)`,
        }}
      >
        {/* Category + Difficulty */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 32, marginRight: 20 }}>{p.categoryIcon}</span>
            <span style={{ fontSize: 40, color: '#38bdf8', fontWeight: 600 }}>{p.category}</span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 20px',
              background: 'rgba(255,255,255,0.06)',
              borderRadius: 30,
              border: `1px solid ${diffConfig.color}40`,
            }}
          >
            <span style={{ fontSize: 32, color: diffConfig.color, fontWeight: 600 }}>{p.difficulty}</span>
            <div style={{ display: 'flex', gap: 4 }}>
              {[1, 2, 3].map((d) => (
                <div
                  key={d}
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: d <= diffConfig.dots ? diffConfig.color : 'rgba(255,255,255,0.15)',
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Skill name */}
        <h1
          style={{
            fontSize: 64,
            fontWeight: 800,
            margin: '24px 0 40px 0',
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
            color: 'white',
          }}
        >
          {p.skillName}
        </h1>

        <div
          style={{
            width: '100%',
            height: 1,
            background: 'linear-gradient(90deg, rgba(56,189,248,0.5), transparent)',
            marginTop: 24,
          }}
        />
      </div>

      {/* Steps */}
      <div
        style={{
          position: 'absolute',
          top: 360,
          left: 60,
          right: 60,
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        {p.steps.map((step, i) => {
          const stepStart = 80 + i * 80;
          const cardProgress = spring({
            frame: frame - stepStart,
            fps,
            from: 0,
            to: 1,
            config: { damping: 16, stiffness: 130, mass: 0.6 },
          });
          const isActive = i === activeStep;
          const isPast = i < activeStep;
          const isFuture = i > activeStep;

          const progressWidth = isActive
            ? interpolate(frame, [stepStart, stepStart + 80], [0, 100], { extrapolateRight: 'clamp' })
            : isPast ? 100 : 0;

          return (
            <div
              key={i}
              style={{
                display: 'flex',
                gap: 20,
                opacity: cardProgress,
                transform: `translateX(${(1 - cardProgress) * 60}px)`,
              }}
            >
              {/* Timeline */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 48 }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 14,
                    background: isPast
                      ? 'linear-gradient(135deg, #10b981, #34d399)'
                      : isActive
                        ? 'linear-gradient(135deg, #38bdf8, #818cf8)'
                        : 'rgba(255,255,255,0.08)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: isPast ? 30 : 28,
                    fontWeight: 700,
                    color: isPast || isActive ? 'white' : 'rgba(255,255,255,0.3)',
                    boxShadow: isActive ? '0 0 20px rgba(56,189,248,0.4)' : 'none',
                  }}
                >
                  {isPast ? '✓' : step.step}
                </div>
                {i < p.steps.length - 1 && (
                  <div
                    style={{
                      width: 2,
                      flex: 1,
                      marginTop: 4,
                      background: isPast ? '#10b981' : 'rgba(255,255,255,0.1)',
                    }}
                  />
                )}
              </div>

              {/* Card */}
              <div
                style={{
                  flex: 1,
                  background: isActive
                    ? 'rgba(56, 189, 248, 0.08)'
                    : 'rgba(255,255,255,0.03)',
                  borderRadius: 20,
                  padding: '24px 28px',
                  border: isActive
                    ? '1px solid rgba(56, 189, 248, 0.3)'
                    : '1px solid rgba(255,255,255,0.06)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* progress fill */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: `${progressWidth}%`,
                    height: 3,
                    background: isActive
                      ? 'linear-gradient(90deg, #38bdf8, #818cf8)'
                      : isPast
                        ? '#10b981'
                        : 'transparent',
                    borderRadius: 2,
                  }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <span style={{ fontSize: 40, marginRight: 10 }}>{step.icon}</span>
                  <span
                    style={{
                      fontSize: 40,
                      fontWeight: 700,
                      color: isActive ? '#38bdf8' : isFuture ? 'rgba(255,255,255,0.4)' : 'white',
                    }}
                  >
                    {step.title}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 38,
                    lineHeight: 1.5,
                    color: isFuture ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.7)',
                  }}
                >
                  {step.detail}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tools used */}
      {p.toolsUsed && (
        <div
          style={{
            position: 'absolute',
            bottom: 200,
            left: 60,
            right: 60,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            opacity: tipOpacity,
          }}
        >
          <span style={{ fontSize: 40, color: 'rgba(255,255,255,0.4)' }}>使用工具</span>
          {p.toolsUsed.map((tool, i) => (
            <div
              key={i}
              style={{
                padding: '8px 18px',
                background: 'rgba(129, 140, 248, 0.12)',
                border: '1px solid rgba(129, 140, 248, 0.25)',
                borderRadius: 10,
                fontSize: 38,
                color: '#a5b4fc',
                fontWeight: 500,
              }}
            >
              {tool}
            </div>
          ))}
        </div>
      )}

      {/* Key tip */}
      <div
        style={{
          position: 'absolute',
          bottom: 60,
          left: 60,
          right: 60,
          opacity: tipOpacity,
          transform: `translateY(${tipY}px)`,
        }}
      >
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(239, 68, 68, 0.05))',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: 16,
            padding: '20px 28px',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <span style={{ fontSize: 32 }}>💡</span>
          <span style={{ fontSize: 35, color: '#fbbf24', lineHeight: 1.5 }}>{p.keyTip}</span>
        </div>
      </div>

      {/* Bottom progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 480], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #38bdf8, #818cf8, #c084fc)',
        }}
      />
    </AbsoluteFill>
  );
};
